import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from './ui/sheet';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Phone,
  MessageCircle,
  Mail,
  MessageSquare,
  ArrowLeft,
  ExternalLink,
  Sparkles,
  Smartphone,
  History,
  Clock,
  Plus,
  Check,
  X
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';

function formatPhoneForWhatsApp(phone) {
  if (!phone) return null;
  let cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }
  return cleaned;
}

function formatPhoneForTel(phone) {
  if (!phone) return null;
  return phone.replace(/[^\d+]/g, '');
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const safeDateStr = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;
  const date = new Date(safeDateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

const interactionTypeLabels = {
  call: 'Phone Call',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'Email',
  inapp_message: 'In-App Message'
};

const interactionTypeIcons = {
  call: Phone,
  sms: Smartphone,
  whatsapp: MessageCircle,
  email: Mail,
  inapp_message: MessageSquare
};

export default function ContactActionsSheet({
  open, onOpenChange, contact }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState('main');
  const [addingNote, setAddingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const queryClient = useQueryClient();

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setView('main');
      setAddingNote(null);
      setNoteText('');
    }
    onOpenChange(isOpen);
  };

  const { data: interactions, isLoading: loadingHistory } = useQuery({
    queryKey: ['contact-interactions', contact?._id],
    queryFn: () => api.get(`/contacts/${contact._id}/interactions`),
    enabled: !!contact?._id && open
  });

  const logInteraction = useMutation({
    mutationFn: ({ contactId, type, notes }) =>
      api.post(`/contacts/${contactId}/interactions`, { type, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['contact-interactions', contact?._id]);
    }
  });

  const updateInteraction = useMutation({
    mutationFn: ({ contactId, interactionId, notes }) =>
      api.put(`/contacts/${contactId}/interactions/${interactionId}`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['contact-interactions', contact?._id]);
      setAddingNote(null);
      setNoteText('');
      toast.success('Note saved');
    }
  });

  if (!contact) return null;

  const { name, contact_email, contact_phone, preferred_language } = contact;
  const phoneForWhatsApp = formatPhoneForWhatsApp(contact_phone);
  const phoneForTel = formatPhoneForTel(contact_phone);

  const handleAction = async (type, href) => {
    // Log the interaction
    try {
      await logInteraction.mutateAsync({
        contactId: contact._id,
        type
      });
    } catch (e) {
      console.error('Failed to log interaction:', e);
    }

    // Open the link
    if (href) {
      window.open(href, '_blank');
    }

    toast.success(`${interactionTypeLabels[type]} logged`);
  };

  const handleInAppMessage = async () => {
    try {
      // 1. Get or Create conversation
      const response = await api.post('/conversations/client', { client_id: contact._id });
      const conversationId = response._id;

      // 2. Log intention
      await logInteraction.mutateAsync({
        contactId: contact._id,
        type: 'inapp_message'
      });
      toast.success('Message started');

      // 3. Navigate to messages
      navigate(`/messages?conversation=${conversationId}`);
    } catch (e) {
      console.error('Failed to start in-app message:', e);
      toast.error('Failed to open conversation');
    }
    handleOpenChange(false);
  };

  const handleSaveNote = (interactionId) => {
    if (!noteText.trim()) return;
    updateInteraction.mutate({
      contactId: contact._id,
      interactionId,
      notes: noteText
    });
  };

  // Main Actions View
  const MainActionsView = () => (
    <>
      <SheetHeader className="text-left pb-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarFallback className="bg-primary/10 text-primary text-lg">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <SheetTitle className="text-xl">{name || 'Unknown Contact'}</SheetTitle>
            <SheetDescription className="flex items-center gap-2 mt-1">
              {preferred_language && (
                <Badge variant="secondary" className="text-xs uppercase">
                  {preferred_language}
                </Badge>
              )}
              {contact_email && (
                <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                  {contact_email}
                </span>
              )}
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <div className="grid grid-cols-2 gap-3 py-4">
        <Button
          variant="outline"
          className={`h-auto py-4 flex flex-col items-center gap-2 relative ${!contact_phone ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted hover:text-foreground'
            }`}
          onClick={() => handleAction('call', phoneForTel ? `tel:${phoneForTel}` : null)}
          disabled={!contact_phone}
          data-testid="contact-action-call"
        >
          <div className="p-2 rounded-full bg-muted text-green-600">
            <Phone className="h-5 w-5" />
          </div>
          <div className="text-center">
            <p className="font-medium text-sm">Call</p>
            <p className="text-xs text-muted-foreground truncate max-w-[120px]">
              {contact_phone || 'No phone'}
            </p>
          </div>
        </Button>

        <Button
          variant="outline"
          className={`h-auto py-4 flex flex-col items-center gap-2 relative ${!contact_phone ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted hover:text-foreground'
            }`}
          onClick={() => handleAction('whatsapp', phoneForWhatsApp ? `https://wa.me/${phoneForWhatsApp}` : null)}
          disabled={!contact_phone}
          data-testid="contact-action-whatsapp"
        >
          <div className="p-2 rounded-full bg-muted text-green-500">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="text-center">
            <p className="font-medium text-sm">WhatsApp</p>
            <p className="text-xs text-muted-foreground truncate max-w-[120px]">
              {contact_phone ? 'Open chat' : 'No phone'}
            </p>
          </div>
        </Button>

        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2 relative hover:bg-muted hover:text-foreground"
          onClick={() => setView('message')}
          data-testid="contact-action-text"
        >
          <div className="p-2 rounded-full bg-muted text-blue-500">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div className="text-center">
            <p className="font-medium text-sm">Message</p>
            <p className="text-xs text-muted-foreground">Choose method</p>
          </div>
        </Button>

        <Button
          variant="outline"
          className={`h-auto py-4 flex flex-col items-center gap-2 relative ${!contact_email ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted hover:text-foreground'
            }`}
          onClick={() => handleAction('email', contact_email ? `mailto:${contact_email}` : null)}
          disabled={!contact_email}
          data-testid="contact-action-email"
        >
          <div className="p-2 rounded-full bg-muted text-orange-500">
            <Mail className="h-5 w-5" />
          </div>
          <div className="text-center">
            <p className="font-medium text-sm">Email</p>
            <p className="text-xs text-muted-foreground truncate max-w-[120px]">
              {contact_email || 'No email'}
            </p>
          </div>
        </Button>
      </div>

      {/* View History Button */}
      <div className="pt-4 border-t">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setView('history')}
          data-testid="contact-view-history"
        >
          <History className="h-4 w-4 mr-2" />
          View History
          {interactions?.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {interactions.length}
            </Badge>
          )}
        </Button>
      </div>
    </>
  );

  // Message Options View
  const MessageOptionsView = () => (
    <>
      <SheetHeader className="text-left pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setView('main')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <SheetTitle className="text-lg">Send Message</SheetTitle>
            <SheetDescription>
              Choose how to message {name}
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <div className="space-y-3 py-4">
        <Button
          variant="default"
          className="w-full h-auto py-4 flex items-center gap-4 justify-start"
          onClick={handleInAppMessage}
          data-testid="contact-action-inapp-message"
        >
          <div className="p-3 rounded-full bg-primary-foreground/20">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="text-left flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">In-App Message</p>
              <Badge variant="secondary" className="text-[10px] bg-primary-foreground/20">
                Recommended
              </Badge>
            </div>
            <p className="text-xs opacity-80 mt-1">
              With translation support
            </p>
          </div>
        </Button>

        <Button
          variant="outline"
          className={`w-full h-auto py-4 flex items-center gap-4 justify-start ${!contact_phone ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          onClick={() => handleAction('sms', phoneForTel ? `sms:${phoneForTel}` : null)}
          disabled={!contact_phone}
          data-testid="contact-action-phone-sms"
        >
          <div className="p-3 rounded-full bg-muted text-blue-500">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="text-left flex-1">
            <p className="font-semibold">Phone SMS</p>
            <p className="text-xs text-muted-foreground mt-1">
              {contact_phone || 'No phone number'}
            </p>
          </div>
          {contact_phone && (
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>
    </>
  );

  // History View
  const HistoryView = () => (
    <>
      <SheetHeader className="text-left pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setView('main')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <SheetTitle className="text-lg">Communication History</SheetTitle>
            <SheetDescription>
              All interactions with {name}
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <ScrollArea className="h-[350px] pr-4">
        {loadingHistory ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : interactions?.length > 0 ? (
          <div className="space-y-3">
            {interactions.map((interaction) => {
              const Icon = interactionTypeIcons[interaction.type] || MessageSquare;
              return (
                <div
                  key={interaction.id}
                  className="p-3 border rounded-lg space-y-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded bg-muted">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {interactionTypeLabels[interaction.type]}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(interaction.created_at)}
                        </p>
                      </div>
                    </div>
                    {!interaction.notes && addingNote !== interaction.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          setAddingNote(interaction.id);
                          setNoteText('');
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Note
                      </Button>
                    )}
                  </div>

                  {interaction.notes && (
                    <p className="text-sm text-muted-foreground pl-8">
                      {interaction.notes}
                    </p>
                  )}

                  {addingNote === interaction.id && (
                    <div className="pl-8 space-y-2">
                      <Textarea
                        placeholder="Add a note..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        className="h-20 text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="h-7"
                          onClick={() => handleSaveNote(interaction.id)}
                          disabled={!noteText.trim() || updateInteraction.isPending}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7"
                          onClick={() => {
                            setAddingNote(null);
                            setNoteText('');
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <History className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No interactions yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Start by calling or messaging this contact
            </p>
          </div>
        )}
      </ScrollArea>
    </>
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-xl max-h-[85vh]">
        {view === 'main' && <MainActionsView />}
        {view === 'message' && <MessageOptionsView />}
        {view === 'history' && <HistoryView />}
      </SheetContent>
    </Sheet>
  );
}
